import React, { useState, useMemo, useEffect, useCallback, memo } from 'react'
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Chip,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as CircleIcon,
    Error as AlertCircleIcon,
    Event as CalendarIcon,
    Chat as ChatIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    NotificationsActive as ReminderIcon,
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { Property } from './DeadlineHome'
import { ApartmentDeadline } from '../../../../models'

// Extended interface that includes the deadlines array
interface DeadlineDataWithDetails {
    id: number
    name: string
    total_deadlines: number
    total_deadlines_completed: number
    total_deadlines_overdue: number
    deadlines: ApartmentDeadline[]
}

interface PropertyDrawerProps {
    property: Property | null
    deadlineData: DeadlineDataWithDetails | null
    isOpen: boolean
    onClose: () => void
}

// Task status for display
type TaskStatus = 'completed' | 'incomplete' | 'overdue'

// Processed task for display
interface DisplayTask {
    id: number
    name: string
    description: string
    status: TaskStatus
    dueDate: string
    formattedDate: string
    messageCount: number
    reminderDate: string | null
    formattedReminderDate: string
}

const useStyles = makeStyles((theme) => ({
    drawer: {
        '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: 480,
            backgroundColor: theme.palette.background.paper,
        },
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: theme.spacing(2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerContent: {
        flex: 1,
    },
    propertyName: {
        fontSize: '1.125rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    propertyLocation: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    headerMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(1.5),
    },
    statusChip: {
        fontWeight: 500,
        fontSize: '0.75rem',
    },
    statusOnTrack: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
    },
    statusAtRisk: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
    },
    statusBehind: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
    },
    progressSection: {
        padding: theme.spacing(2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: theme.spacing(2),
    },
    statItem: {
        textAlign: 'center',
    },
    statValue: {
        fontSize: '1.5rem',
        fontWeight: 700,
    },
    statValueSuccess: {
        color: theme.palette.success.main,
    },
    statValueMuted: {
        color: theme.palette.text.secondary,
    },
    statValueError: {
        color: theme.palette.error.main,
    },
    statLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    progressContainer: {
        marginTop: theme.spacing(2),
    },
    progressHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0.5),
    },
    progressLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    progressBar: {
        height: 8,
        backgroundColor: theme.palette.action.hover,
        borderRadius: 4,
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        backgroundColor: theme.palette.primary.main,
        borderRadius: 4,
        transition: 'width 0.3s ease',
    },
    searchContainer: {
        padding: theme.spacing(2, 2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    searchInput: {
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.background.default,
            '& fieldset': {
                borderColor: theme.palette.divider,
            },
            '&:hover fieldset': {
                borderColor: theme.palette.primary.light,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1, 1.5),
            fontSize: '0.875rem',
        },
    },
    tabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tabs: {
        minHeight: 40,
    },
    tab: {
        minHeight: 40,
        fontSize: '0.75rem',
        textTransform: 'none',
        minWidth: 'auto',
        padding: theme.spacing(1, 2),
    },
    tabContent: {
        padding: theme.spacing(2.5),
        overflowY: 'auto',
        height: 'calc(100vh - 440px)',
    },
    taskList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    taskItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5),
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.action.hover,
        transition: 'background-color 0.2s',
        '&:hover': {
            backgroundColor: theme.palette.action.selected,
        },
    },
    taskContent: {
        flex: 1,
        minWidth: 0,
    },
    taskName: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    taskDescription: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    taskMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginTop: theme.spacing(0.5),
    },
    taskMetaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    taskMetaIcon: {
        fontSize: '0.875rem',
    },
    statusBadge: {
        fontSize: '0.625rem',
        height: 20,
        flexShrink: 0,
    },
    statusBadgeCompleted: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
    },
    statusBadgeIncomplete: {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.grey[700],
    },
    statusBadgeOverdue: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.dark,
    },
    emptyMessage: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    statusIconSuccess: {
        color: theme.palette.success.main,
        fontSize: '1rem',
    },
    statusIconMuted: {
        color: theme.palette.text.disabled,
        fontSize: '1rem',
    },
    statusIconError: {
        color: theme.palette.error.main,
        fontSize: '1rem',
    },
}))

// Helper to determine if a deadline is overdue
const isOverdue = (dueDate: string, isComplete: boolean): boolean => {
    if (isComplete || !dueDate) return false
    const due = new Date(dueDate)
    const now = new Date()
    return due < now
}

// Format date once during conversion
const formatDate = (dateStr: string): string => {
    if (!dateStr) return ''
    try {
        return new Date(dateStr).toLocaleDateString()
    } catch {
        return ''
    }
}

// Convert ApartmentDeadline to DisplayTask
const convertToDisplayTask = (deadline: ApartmentDeadline): DisplayTask => {
    const dueDate = deadline.deadline?.date || ''
    const isComplete = deadline.complete_status
    const reminderDate = deadline.deadline?.reminder_date || null

    let status: TaskStatus = 'incomplete'
    if (isComplete) {
        status = 'completed'
    } else if (isOverdue(dueDate, isComplete)) {
        status = 'overdue'
    }

    return {
        id: deadline.id,
        name: deadline.deadline?.name || 'Unnamed Deadline',
        description: deadline.deadline?.description || '',
        status,
        dueDate,
        formattedDate: formatDate(dueDate),
        messageCount: deadline.message_count || 0,
        reminderDate,
        formattedReminderDate: reminderDate ? formatDate(reminderDate) : '',
    }
}

// Status label lookup
const STATUS_LABELS: Record<TaskStatus, string> = {
    completed: 'Completed',
    incomplete: 'Pending',
    overdue: 'Overdue',
}

// Property status labels
const PROPERTY_STATUS_LABELS: Record<string, string> = {
    'on-track': 'On Track',
    'at-risk': 'At Risk',
    behind: 'Behind Schedule',
}

// Memoized TaskItem component - uses its own useStyles call
interface TaskItemProps {
    task: DisplayTask
}

const TaskItem = memo<TaskItemProps>(({ task }) => {
    const classes = useStyles()

    const getStatusIcon = () => {
        switch (task.status) {
            case 'completed':
                return <CheckCircleIcon className={classes.statusIconSuccess} />
            case 'incomplete':
                return <CircleIcon className={classes.statusIconMuted} />
            case 'overdue':
                return <AlertCircleIcon className={classes.statusIconError} />
            default:
                return null
        }
    }

    const getStatusBadgeClass = () => {
        switch (task.status) {
            case 'completed':
                return `${classes.statusBadge} ${classes.statusBadgeCompleted}`
            case 'incomplete':
                return `${classes.statusBadge} ${classes.statusBadgeIncomplete}`
            case 'overdue':
                return `${classes.statusBadge} ${classes.statusBadgeOverdue}`
            default:
                return classes.statusBadge
        }
    }

    return (
        <Box className={classes.taskItem}>
            {getStatusIcon()}
            <Box className={classes.taskContent}>
                <Typography className={classes.taskName}>
                    {task.name}
                </Typography>
                {task.description && (
                    <Typography className={classes.taskDescription}>
                        {task.description}
                    </Typography>
                )}
                <Box className={classes.taskMeta}>
                    {task.formattedDate && (
                        <Box className={classes.taskMetaItem}>
                            <CalendarIcon className={classes.taskMetaIcon} />
                            {task.formattedDate}
                        </Box>
                    )}
                    {task.messageCount > 0 && (
                        <Box className={classes.taskMetaItem}>
                            <ChatIcon className={classes.taskMetaIcon} />
                            {task.messageCount} messages
                        </Box>
                    )}
                    {task.formattedReminderDate && (
                        <Box className={classes.taskMetaItem}>
                            <ReminderIcon className={classes.taskMetaIcon} />
                            Reminder: {task.formattedReminderDate}
                        </Box>
                    )}
                </Box>
            </Box>
            <Chip
                label={STATUS_LABELS[task.status]}
                size="small"
                className={getStatusBadgeClass()}
            />
        </Box>
    )
})
TaskItem.displayName = 'TaskItem'

// Memoized TaskList component
interface TaskListProps {
    tasks: DisplayTask[]
    emptyMessage: string
}

const TaskList = memo<TaskListProps>(({ tasks, emptyMessage }) => {
    const classes = useStyles()

    if (tasks.length === 0) {
        return (
            <Typography className={classes.emptyMessage}>
                {emptyMessage}
            </Typography>
        )
    }

    return (
        <Box className={classes.taskList}>
            {tasks.map((task) => (
                <TaskItem key={task.id} task={task} />
            ))}
        </Box>
    )
})
TaskList.displayName = 'TaskList'

export const DeadlinePropertyDrawer: React.FC<PropertyDrawerProps> = ({
    property,
    deadlineData,
    isOpen,
    onClose,
}) => {
    const classes = useStyles()
    const [tabValue, setTabValue] = useState(0)
    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')

    // Debounce search input - only set debounced value after delay
    useEffect(() => {
        if (searchQuery === debouncedSearch) return // Skip if already equal
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery)
        }, 200)
        return () => clearTimeout(timer)
    }, [searchQuery, debouncedSearch])

    // Reset search and tab when drawer closes
    useEffect(() => {
        if (!isOpen) {
            // Use functional updates to avoid dependencies
            setSearchQuery('')
            setDebouncedSearch('')
            setTabValue(0)
        }
    }, [isOpen])

    // Process deadline data into display tasks
    const tasks = useMemo((): DisplayTask[] => {
        if (
            !deadlineData?.deadlines ||
            !Array.isArray(deadlineData.deadlines)
        ) {
            return []
        }
        return deadlineData.deadlines.map(convertToDisplayTask)
    }, [deadlineData])

    // Filter tasks based on debounced search query
    const filteredTasks = useMemo(() => {
        if (!debouncedSearch.trim()) return tasks
        const query = debouncedSearch.toLowerCase().trim()
        return tasks.filter(
            (t) =>
                t.name.toLowerCase().includes(query) ||
                t.description.toLowerCase().includes(query),
        )
    }, [tasks, debouncedSearch])

    // Memoize filtered task lists
    const completedTasks = useMemo(
        () => filteredTasks.filter((t) => t.status === 'completed'),
        [filteredTasks],
    )
    const incompleteTasks = useMemo(
        () => filteredTasks.filter((t) => t.status === 'incomplete'),
        [filteredTasks],
    )
    const overdueTasks = useMemo(
        () => filteredTasks.filter((t) => t.status === 'overdue'),
        [filteredTasks],
    )

    // Memoize handlers
    const handleTabChange = useCallback(
        (_event: React.ChangeEvent<{}>, newValue: number) => {
            setTabValue(newValue)
        },
        [],
    )

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(e.target.value)
        },
        [],
    )

    const handleClearSearch = useCallback(() => {
        setSearchQuery('')
        setDebouncedSearch('')
    }, [])

    // Get current tab content
    const currentTasks = useMemo(() => {
        switch (tabValue) {
            case 0:
                return filteredTasks
            case 1:
                return completedTasks
            case 2:
                return incompleteTasks
            case 3:
                return overdueTasks
            default:
                return filteredTasks
        }
    }, [tabValue, filteredTasks, completedTasks, incompleteTasks, overdueTasks])

    const emptyMessage = useMemo(() => {
        const hasSearch = debouncedSearch.trim()
        switch (tabValue) {
            case 0:
                return hasSearch
                    ? 'No deadlines match your search'
                    : 'No deadlines found'
            case 1:
                return hasSearch
                    ? 'No completed deadlines match your search'
                    : 'No completed deadlines'
            case 2:
                return hasSearch
                    ? 'No pending deadlines match your search'
                    : 'No pending deadlines'
            case 3:
                return hasSearch
                    ? 'No overdue deadlines match your search'
                    : 'No overdue deadlines'
            default:
                return 'No deadlines found'
        }
    }, [tabValue, debouncedSearch])

    // Early return if no property
    if (!property) return null

    // Compute property-specific values
    const progressPercentage =
        property.totalTasks > 0
            ? Math.round((property.completedTasks / property.totalTasks) * 100)
            : 0

    const remainingTasks = Math.max(
        0,
        property.totalTasks - property.completedTasks - property.overdueTasks,
    )

    const getPropertyStatusChipClass = () => {
        switch (property.status) {
            case 'on-track':
                return `${classes.statusChip} ${classes.statusOnTrack}`
            case 'at-risk':
                return `${classes.statusChip} ${classes.statusAtRisk}`
            case 'behind':
                return `${classes.statusChip} ${classes.statusBehind}`
            default:
                return classes.statusChip
        }
    }

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            className={classes.drawer}
        >
            {/* Header */}
            <Box className={classes.header}>
                <Box className={classes.headerContent}>
                    <Typography className={classes.propertyName}>
                        {property.name}
                    </Typography>
                    {property.location && (
                        <Typography className={classes.propertyLocation}>
                            {property.location}
                        </Typography>
                    )}
                    <Box className={classes.headerMeta}>
                        <Chip
                            label={
                                PROPERTY_STATUS_LABELS[property.status] ||
                                property.status
                            }
                            size="small"
                            className={getPropertyStatusChipClass()}
                        />
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Progress Summary */}
            <Box className={classes.progressSection}>
                <Box className={classes.statsGrid}>
                    <Box className={classes.statItem}>
                        <Typography
                            className={`${classes.statValue} ${classes.statValueSuccess}`}
                        >
                            {property.completedTasks}
                        </Typography>
                        <Typography className={classes.statLabel}>
                            Completed
                        </Typography>
                    </Box>
                    <Box className={classes.statItem}>
                        <Typography
                            className={`${classes.statValue} ${classes.statValueMuted}`}
                        >
                            {remainingTasks}
                        </Typography>
                        <Typography className={classes.statLabel}>
                            Remaining
                        </Typography>
                    </Box>
                    <Box className={classes.statItem}>
                        <Typography
                            className={`${classes.statValue} ${classes.statValueError}`}
                        >
                            {property.overdueTasks}
                        </Typography>
                        <Typography className={classes.statLabel}>
                            Overdue
                        </Typography>
                    </Box>
                </Box>

                {/* Progress bar */}
                <Box className={classes.progressContainer}>
                    <Box className={classes.progressHeader}>
                        <Typography className={classes.progressLabel}>
                            Progress
                        </Typography>
                        <Typography className={classes.progressLabel}>
                            {progressPercentage}%
                        </Typography>
                    </Box>
                    <Box className={classes.progressBar}>
                        <Box
                            className={classes.progressFill}
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Search Bar */}
            <Box className={classes.searchContainer}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search deadlines..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className={classes.searchInput}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleClearSearch}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
            </Box>

            {/* Tasks Tabs */}
            <Box className={classes.tabsContainer}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    className={classes.tabs}
                >
                    <Tab
                        label={`All (${filteredTasks.length})`}
                        className={classes.tab}
                    />
                    <Tab
                        label={`Done (${completedTasks.length})`}
                        className={classes.tab}
                    />
                    <Tab
                        label={`Pending (${incompleteTasks.length})`}
                        className={classes.tab}
                    />
                    <Tab
                        label={`Overdue (${overdueTasks.length})`}
                        className={classes.tab}
                    />
                </Tabs>
            </Box>

            {/* Tab Content */}
            <Box className={classes.tabContent}>
                <TaskList tasks={currentTasks} emptyMessage={emptyMessage} />
            </Box>
        </Drawer>
    )
}

export default DeadlinePropertyDrawer
